/**
 * Phone Input Component
 * Phone number input with country code
 * 
 * @author Varun Pandey
 */

import React, { useState, useEffect } from 'react';
import { cn } from '../../utils/cn';
import { Loader } from './Loader';
import { formatIndianPhoneForDisplay, sanitizeIndianPhoneInput } from '../../utils/phone';

export interface PhoneInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  label?: string;
  error?: string;
  /** When true, error ring is still applied but error message is not rendered (e.g. when showing errors via toast). */
  hideErrorText?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  defaultCountry?: string;
  isLoading?: boolean;
}

export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      className,
      label,
      error,
      hideErrorText = true,
      value,
      onChange,
      defaultCountry = '+91',
      isLoading = false,
      ...props
    },
    ref
  ) => {
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = useState(value || '');
    const phoneNumber = isControlled ? (value || '') : internalValue;
    const [showError, setShowError] = useState<boolean>(Boolean(error));

    useEffect(() => {
      if (isControlled && value !== undefined) {
        // Sync internal state if controlled value changes externally
        setInternalValue(value);
      }
    }, [value, isControlled]);

    // Whenever a new error comes from parent, show the red outline again
    useEffect(() => {
      if (error) {
        setShowError(true);
      } else {
        setShowError(false);
      }
    }, [error]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const limitedValue = sanitizeIndianPhoneInput(e.target.value);
      if (!isControlled) {
        setInternalValue(limitedValue);
      }
       // Clear visual error as soon as user modifies the value
      if (showError) {
        setShowError(false);
      }
      onChange?.(limitedValue); // Always pass value without spaces
    };

    return (
      <div className="w-full">
        {label && (
          <label className="mb-2 block text-sm font-medium text-white">
            {label}
          </label>
        )}
        <div className="relative flex items-center">
          <div className="absolute left-4 right-auto flex items-center justify-center h-12 text-sm text-[#8E8E93]">
            {defaultCountry} |
          </div>
          <input
            type="tel"
            className={cn(
              'flex h-12 w-full rounded-[0.75rem] bg-transparent border border-[#3A3A3C] pl-16 pr-4 py-3 text-sm text-white placeholder:text-[#8E8E93] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] disabled:cursor-not-allowed disabled:opacity-50',
              showError && error && 'ring-2 ring-destructive',
              className
            )}
            ref={ref}
            value={phoneNumber ? formatIndianPhoneForDisplay(phoneNumber) : ''}
            onChange={handleChange}
            placeholder="Enter your mobile number"
            {...props}
          />
          {isLoading && (
            <div className="absolute right-4 left-auto flex items-center justify-center h-12">
              <Loader size="small" />
            </div>
          )}
        </div>
        {error && showError && !hideErrorText && (
          <p className="mt-1 text-xs text-destructive">
            {error}
          </p>
        )}
      </div>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';
